<template>
  <RouterLink
    data-testid="shop-product-card"
    class="relative flex max-w-[312px] min-w-[240px] flex-[1_1_312px] shrink flex-col rounded-lg border border-main"
    :class="{ 'border-primary': isInCart, 'state-layer-neutral cursor-pointer': !disabled, 'cursor-not-allowed': disabled }"
    :to="outOfStock ? {} : { name: 'shopProductDetails', params: { productId } }"
  >
    <div
      class="flex h-[162px] shrink-0 justify-center overflow-hidden rounded-t-lg"
      :class="{ 'bg-surface-elevation-2': !product.imageUrl, 'bg-solid': product.imageUrl }"
    >
      <template v-if="product.imageUrl">
        <img :src="product.imageUrl" class="h-full w-full object-cover" />
      </template>
      <template v-else>
        <div class="relative flex h-full w-full flex-col items-center justify-center">
          <div class="mb-5 ml-30 w-[93px] txt-body-s-bold text-disabled">
            {{ t('FestivalShop.products.details.no_image') }}
          </div>
          <img src="/assets/images/product_no_img.svg" class="absolute bottom-0 left-1 h-[90%] w-[90%]" />
        </div>
      </template>
    </div>
    <div class="description-section flex h-full flex-col justify-between p-4">
      <div>
        <h2 data-testid="shop-product-name" class="line-clamp-2 txt-headline-m text-main">{{ props.product?.name }}</h2>
        <p class="mt-1 line-clamp-2 txt-body-s text-main">{{ props.product?.shortDescription }}</p>
      </div>
      <p data-testid="shop-product-price" class="mt-6 text-body-l text-main">
        <span v-if="showFromLabel" class="mr-1">{{ t('FestivalShop.products.details.price_from') }}</span>
        {{ displayPrice }}
      </p>
    </div>
    <!-- eslint-disable-next-line better-tailwindcss/no-restricted-classes -- the palette has no translucent scrim token, and no opacity utility applies to a bare overlay (REV-903) -->
    <div v-if="outOfStock" class="absolute top-0 left-0 h-full w-full bg-[#ffffffb5]" />
    <ChipBadge
      v-if="expired"
      data-testid="shop-product-expired"
      color="default"
      rounded
      size="m"
      type="solid"
      class="absolute top-3 right-3"
    >
      {{ t('FestivalShop.products.details.expired') }}
    </ChipBadge>
    <ChipBadge
      v-if="outOfStock"
      data-testid="shop-product-out-of-stock"
      color="default"
      rounded
      size="m"
      type="solid"
      class="absolute top-3 right-3"
    >
      {{ t('FestivalShop.products.details.expired') }}
    </ChipBadge>
    <ChipBadge v-if="isInCart" data-testid="shop-product-in-cart" color="purple" rounded size="m" class="absolute top-3 right-3">
      {{ `${numberInCart} in cart` }}
    </ChipBadge>
  </RouterLink>
</template>
<script lang="ts" setup>
import { ChipBadge } from '@ramp106/omrjs-core-ui'
import { storeToRefs } from 'pinia'
import { computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'

import { FestivalShopCartKey } from '@/composables/useFestivalShopCart'
import type { FestivalShopProductsQuery } from '@/gql/myomr'
import { toPlainId } from '@/helpers/globalIdHelper'
import { useLocaleStore } from '@/stores'

import { formatPrice, formatPriceRange } from '../helpers'

type Product = FestivalShopProductsQuery['festivalShopProducts']['nodes'][number]

const props = defineProps<{
  product: Product
}>()

const festivalShopCart = inject(FestivalShopCartKey)
if (!festivalShopCart) {
  throw new Error('FestivalShopCart not provided')
}

const { t } = useI18n()
const { locale } = storeToRefs(useLocaleStore())

const expiryDateString = computed(() => props.product?.productCategory?.expiresAt ?? null)
const expiryDate = computed(() => (expiryDateString.value ? new Date(expiryDateString.value) : null))
const expired = computed(() => (expiryDate.value ? expiryDate.value < new Date() : false))
const outOfStock = computed(() => {
  return props.product.eventMaximumReached || props.product.eventMaximumLeft === 0 || props.product.maximumLeft === 0
})
const disabled = computed(() => outOfStock.value || expired.value)

const variantPrices = computed(() => {
  return props.product.variants
    .map((v) => v.price)
    .filter((p): p is number => p != null)
    .sort((a, b) => a - b)
})

const showPriceRange = computed(() => {
  if (props.product.variants.length <= 1 || variantPrices.value.length < 2) return false
  const minPrice = variantPrices.value[0]
  const maxPrice = variantPrices.value.at(-1)
  return minPrice === 0 && maxPrice != null && maxPrice > minPrice
})

const hasNonZeroVariantPrice = computed(() => variantPrices.value.some((price) => price > 0))

const showFromLabel = computed(() => {
  return props.product.variants.length > 1 && !showPriceRange.value && hasNonZeroVariantPrice.value
})

const displayPrice = computed(() => {
  if (!variantPrices.value.length) return '-NA-'
  if (showPriceRange.value) {
    return formatPriceRange(variantPrices.value[0], variantPrices.value.at(-1) ?? variantPrices.value[0], locale.value, true)
  }
  return formatPrice(variantPrices.value[0], locale.value, true)
})

const isInCart = computed(() => {
  return festivalShopCart.isInCart(props.product.id)
})

const numberInCart = computed(() => {
  return festivalShopCart.numberInCart(props.product.id)
})

const productId = computed(() => toPlainId(props.product.id))
</script>
